import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidInbox } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { hasRole, SETTINGS_FINANCE_ROLES } from '@/auth/roles';
import { getReferralCostHistory, getReferralCosts, setReferralCost } from '@/api/finance.api';
import { ApiError } from '@/api/errors';
import { Banner, type BannerTone } from '@/components/common/Banner';
import { StatusChip } from '@/components/common/StatusChip';
import { FormField } from '@/components/form/FormField';
import { DataTable, type DataTableColumn } from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { formatCurrencyEGP, formatDateOnly } from '@/lib/format';
import { showSuccessToast } from '@/lib/toast';
import type { ReferralCostRow, ReferralCostVersion } from '@/types/shared';
import './ReferralCostManager.css';

function partnerLabel(
  row: { partnerLabelAr: string; partnerLabelEn: string } | null | undefined,
  language: string,
): string {
  if (!row) return '';
  return language.startsWith('ar') ? row.partnerLabelAr : row.partnerLabelEn;
}

/**
 * Referral Costs tab (`/settings/finance`, tab 3, T151) — `docs/architecture/
 * 16-referral-partners.md` §4/§7/§12: a faithful clone of `SchoolCostManager.tsx` with
 * `partner` in place of `school` (per the T151 implementer warning — "mirror, do not invent").
 * A `DataTable` of every active referral partner with its current per-student EGP cost;
 * `costAmount: null` is a real "Not configured" state (FR-5/AC-3), never rendered as `0`.
 * Edit/Set-cost + History are Admin-only (same RBAC precedent as school costs — the
 * `/settings/finance` route itself is Admin-only; Accounts Officer's read access lives on the
 * separate `/finance` overview screen, not here).
 */
export function ReferralCostManager() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user?.role, SETTINGS_FINANCE_ROLES);

  const [partners, setPartners] = useState<ReferralCostRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [banner, setBanner] = useState<{ tone: BannerTone; text: string } | null>(null);

  const [panelPartner, setPanelPartner] = useState<ReferralCostRow | null>(null);
  const [costInput, setCostInput] = useState('');
  const [costError, setCostError] = useState<string | undefined>();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [historyPartner, setHistoryPartner] = useState<ReferralCostRow | null>(null);
  const [history, setHistory] = useState<ReferralCostVersion[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getReferralCosts()
      .then((res) => {
        if (!cancelled) setPartners(res.partners);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('financeSettings.referralCosts.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken, t]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  const filtered = (partners ?? []).filter((row) => {
    const needle = searchInput.trim().toLocaleLowerCase();
    if (!needle) return true;
    return (
      row.partnerLabelAr.toLocaleLowerCase().includes(needle) ||
      row.partnerLabelEn.toLocaleLowerCase().includes(needle)
    );
  });

  function openPanel(row: ReferralCostRow) {
    setBanner(null);
    setHistoryPartner(null);
    setCostInput(row.costAmount ?? '');
    setCostError(undefined);
    setSaveError(null);
    setPanelPartner(row);
  }

  function closePanel() {
    setPanelPartner(null);
    setCostError(undefined);
    setSaveError(null);
  }

  function isValidAmount(value: string): boolean {
    if (value.trim() === '') return false;
    const num = Number(value);
    return !Number.isNaN(num) && num >= 0;
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!panelPartner) return;
    setSaveError(null);

    if (!isValidAmount(costInput)) {
      setCostError(t('financeSettings.referralCosts.costInvalid'));
      return;
    }
    setCostError(undefined);

    setSaving(true);
    try {
      const updated = await setReferralCost(panelPartner.partnerId, {
        costAmount: costInput.trim(),
      });
      setPartners((prev) =>
        prev ? prev.map((p) => (p.partnerId === updated.partnerId ? updated : p)) : prev,
      );
      const message = t('financeSettings.referralCosts.saveSuccess');
      setBanner({ tone: 'success', text: message });
      showSuccessToast(message);
      setPanelPartner(null);
      setHistory(null);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const fieldError = apiError.details.find((d) => d.field === 'costAmount');
      if (fieldError) {
        setCostError(fieldError.message);
      } else {
        setSaveError(
          apiError.code === 'NETWORK_ERROR'
            ? t('financeSettings.referralCosts.networkError')
            : apiError.message || t('financeSettings.referralCosts.genericError'),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function fetchHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    getReferralCostHistory()
      .then((res) => setHistory(res.versions))
      .catch(() => setHistoryError(t('financeSettings.referralCosts.history.loadError')))
      .finally(() => setHistoryLoading(false));
  }

  function openHistory(row: ReferralCostRow) {
    setPanelPartner(null);
    setHistoryPartner(row);
    setHistory(null);
    fetchHistory();
  }

  function closeHistory() {
    setHistoryPartner(null);
  }

  const columns: DataTableColumn<ReferralCostRow>[] = [
    {
      key: 'partner',
      header: t('financeSettings.referralCosts.columns.partner'),
      toggleable: false,
      render: (row) => <bdi>{partnerLabel(row, i18n.language)}</bdi>,
    },
    {
      key: 'currentCost',
      header: t('financeSettings.referralCosts.columns.currentCost'),
      render: (row) =>
        row.costAmount == null ? (
          <StatusChip label={t('financeSettings.referralCosts.notConfigured')} tone="neutral" />
        ) : (
          <bdi>{formatCurrencyEGP(Number(row.costAmount), i18n.language)}</bdi>
        ),
    },
    {
      key: 'effectiveSince',
      header: t('financeSettings.referralCosts.columns.effectiveSince'),
      render: (row) =>
        row.effectiveFrom ? <bdi>{formatDateOnly(row.effectiveFrom, i18n.language)}</bdi> : '—',
    },
    {
      key: 'actions',
      header: t('financeSettings.referralCosts.columns.actions'),
      toggleable: false,
      cellClassName: 'fa-referral-costs__actions-cell',
      render: (row) =>
        canEdit ? (
          <div className="fa-referral-costs__row-actions">
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              aria-label={
                row.costAmount == null
                  ? t('financeSettings.referralCosts.setCostAriaLabel', {
                      partner: partnerLabel(row, i18n.language),
                    })
                  : t('financeSettings.referralCosts.editAriaLabel', {
                      partner: partnerLabel(row, i18n.language),
                    })
              }
              onClick={() => openPanel(row)}
            >
              {row.costAmount == null
                ? t('financeSettings.referralCosts.setCostButton')
                : t('financeSettings.referralCosts.editButton')}
            </button>
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              aria-label={t('financeSettings.referralCosts.historyAriaLabel', {
                partner: partnerLabel(row, i18n.language),
              })}
              onClick={() => openHistory(row)}
            >
              {t('financeSettings.referralCosts.historyButton')}
            </button>
          </div>
        ) : (
          '—'
        ),
    },
  ];

  const emptyState = searchInput.trim() ? (
    <EmptyState
      icon={BiSolidInbox}
      title={t('financeSettings.referralCosts.empty.noMatchesTitle')}
      hint={t('financeSettings.referralCosts.empty.noMatchesHint')}
      action={
        <button
          type="button"
          className="fa-btn fa-btn--secondary"
          onClick={() => setSearchInput('')}
        >
          {t('financeSettings.referralCosts.empty.clearSearch')}
        </button>
      }
    />
  ) : (
    <EmptyState
      icon={BiSolidInbox}
      title={t('financeSettings.referralCosts.empty.noPartnersTitle')}
      hint={t('financeSettings.referralCosts.empty.noPartnersHint')}
    />
  );

  const historyVersions = (history ?? []).filter((v) => v.partnerId === historyPartner?.partnerId);

  const historyColumns: DataTableColumn<ReferralCostVersion>[] = [
    {
      key: 'effectiveFrom',
      header: t('financeSettings.referralCosts.history.columns.effectiveFrom'),
      render: (v) => <bdi>{formatDateOnly(v.effectiveFrom, i18n.language)}</bdi>,
    },
    {
      key: 'cost',
      header: t('financeSettings.referralCosts.history.columns.cost'),
      render: (v) => <bdi>{formatCurrencyEGP(Number(v.costAmount), i18n.language)}</bdi>,
    },
    {
      key: 'current',
      header: t('financeSettings.referralCosts.history.columns.current'),
      render: (v) =>
        v.isCurrent ? (
          <StatusChip
            label={t('financeSettings.referralCosts.history.currentBadge')}
            tone="success"
          />
        ) : null,
    },
  ];

  return (
    <div className="fa-referral-costs">
      {banner && (
        <Banner tone={banner.tone} assertive={banner.tone === 'danger'}>
          {banner.text}
        </Banner>
      )}

      {panelPartner && (
        <form className="fa-form-section fa-referral-costs__panel" onSubmit={handleSave} noValidate>
          <h2 className="fa-form-section__title">
            {panelPartner.costAmount == null
              ? t('financeSettings.referralCosts.formTitleSet', {
                  partner: partnerLabel(panelPartner, i18n.language),
                })
              : t('financeSettings.referralCosts.formTitleEdit', {
                  partner: partnerLabel(panelPartner, i18n.language),
                })}
          </h2>

          {/* Always-visible, non-dismissible one-time-recognition notice, mirroring the
              school-cost tab's GAP-2 notice — except this one also explains the T144 §5 Rule 1
              recalculation behavior (partner-change recalculates to the NEW partner's current
              cost; a same-partner cost change never rewrites an already-recognized student). */}
          <Banner tone="info">
            {t('financeSettings.referralCosts.oneTimeNotice', {
              partner: partnerLabel(panelPartner, i18n.language),
            })}
          </Banner>

          <div className="fa-field-row">
            <FormField
              id="referral-cost-amount"
              label={t('financeSettings.referralCosts.costLabel')}
              required
              error={costError}
            >
              <input
                type="number"
                min="0"
                step="0.01"
                className="fa-ltr-isolate"
                value={costInput}
                disabled={saving}
                onChange={(e) => {
                  setCostInput(e.target.value);
                  setCostError(undefined);
                }}
              />
            </FormField>
          </div>

          <div className="fa-form-actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={saving}
              onClick={closePanel}
            >
              {t('financeSettings.referralCosts.cancelButton')}
            </button>
            <button type="submit" className="fa-btn fa-btn--primary" disabled={saving}>
              {saving
                ? t('financeSettings.referralCosts.savingButton')
                : t('financeSettings.referralCosts.saveButton')}
            </button>
          </div>
          {saveError && (
            <p className="fa-field__error" role="alert">
              {saveError}
            </p>
          )}
        </form>
      )}

      {historyPartner && (
        <div className="fa-referral-costs__history">
          <div className="fa-referral-costs__history-header">
            <h2 className="fa-referral-costs__history-title">
              {t('financeSettings.referralCosts.history.title', {
                partner: partnerLabel(historyPartner, i18n.language),
              })}
            </h2>
            <button type="button" className="fa-btn fa-btn--secondary" onClick={closeHistory}>
              {t('financeSettings.referralCosts.history.closeButton')}
            </button>
          </div>
          <DataTable
            columns={historyColumns}
            rows={historyVersions}
            rowKey={(v) => v.versionId}
            caption={t('financeSettings.referralCosts.history.tableCaption', {
              partner: partnerLabel(historyPartner, i18n.language),
            })}
            meta={null}
            loading={historyLoading}
            error={historyError}
            onRetry={fetchHistory}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
            showExportButton={false}
            emptyState={
              <EmptyState
                icon={BiSolidInbox}
                title={t('financeSettings.referralCosts.history.empty.title')}
                hint={t('financeSettings.referralCosts.history.empty.hint')}
              />
            }
          />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(row) => String(row.partnerId)}
        caption={t('financeSettings.referralCosts.tableCaption')}
        meta={null}
        loading={loading}
        error={loadError}
        onRetry={refetch}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('financeSettings.referralCosts.searchPlaceholder'),
          ariaLabel: t('financeSettings.referralCosts.searchAriaLabel'),
        }}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        showExportButton={false}
        emptyState={emptyState}
      />
    </div>
  );
}
